/* eslint-disable @typescript-eslint/no-shadow */
import { handle } from 'redux-pack';

import {
	LOAD_CHAT_DIRECT_CHAT,
	LOAD_ROOM_CHAT,
	POST_CHAT,
	CREATE_ROOM,
	NEW_MESSAGE,
	NEW_ROOM,
	SET_SELECTED_ROOM,
	SET_SELECTED_USER,
	CREATE_CONVO,
	SET_DIRECT_MESSAGES_SEEN,
	SET_ROOM_MESSAGES_SEEN,
	SET_MODERATED_LEGACY_COMMENTS_SEEN,
	SET_MODERATED_EVENTS_MESSAGES_SEEN,
	SET_GR_USER,
	GET_APPROVED_COMMENT,
	GET_MODERATED_COMMENTS,
	DELETE_COMMENT,
	UNDELETE_COMMENT,
	VIEW_MODERATED_COMMENTS,
	UNELEVATE_COMMENT,
	ELEVATE_COMMENT,
	setSelectedRoom,
	setSelectedUser,
	viewModeratedComments,
	CREATE_MODERATED_EVENTS_MESSAGES,
	DELETE_MODERATED_EVENTS_MESSAGES,
	GET_PRESENTER_MESSAGES,
	CLEAR_CHAT_DIRECT_CHAT,
	REMOVE_CHAT_MESSAGE,
	CLEAR_CHAT_DIRECT_CHAT_KEY,
	LOAD_CHAT_ROOM_MESSAGES,
	CLEAR_CHAT_ROOM_MESSAGES,
	DELETE_CHAT_ROOM,
} from '../actions/chat';

import type { ConnectedUser } from '../types/connection';
import * as Alerts from '../../components/general-components/alert-service';
import {
	getDisplayChatMessage,
	getNameOrInitials,
	stripHtml,
} from '../../utils/utils';
import type { MessagesSeenResult } from '../types/connection';
import { GreenroomState, StringIndexObject } from '../types/types';
import type { Shot, ShotFeed } from '../types/shots';
import { RoomStates } from '../../types/enums';
import { setActiveTab, setRightDrawerOpen } from '../actions/tabs';
import { TABS } from '../types/tabs';
import { ProfileMask } from '../types/masks';
import {
	EMessageTypes,
	EStatus,
} from '../types/chat';
import type {
	ChatMessage,
	ChatMessages,
	LegacyModeratedChatMessages,
	Document,
	ChatPost,
	ChatMeta,
	ChatConvo,
	ChatRoom,
	EventsMessageGif,
	EventsMessageImage,
	EventsMessage,
	EventsMessageChat,
	EventsMessageQuestion,
	EventsMessageComment,
	DisplayChatMessage,
	GeneralEventsMessage,
	GeneralEventsMessageMap,
	ChatReducerState,
} from '../types/chat';

export function makeId(emails: string[]) {
	// eslint-disable-next-line no-param-reassign
	emails = emails.sort();
	return emails.join('.');
}

interface Action {
	type: string;
	payload: any;
}

export const initialChatState: ChatReducerState = {
	directMessages: {},
	rooms: [],
	selectedRoom: null,
	selectedUser: null,
	lastUnseen: null,
	gr_user: 0,
	legacyModeratedComments: {},
	moderatedEventsMessages: new Map(),
	shouldViewModerated: false,
};

const defaultChatMessage: ChatMessage = {
	chat_message: 0,
	name: '',
	location: '',
	comment: '',
	parent: 0,
	email: null,
	elevated: null,
	approved: 0,
	created: '',
	deleted: 0,
	image: '',
	first_name: null,
	last_name: null,
	avatar: null,
	replies: [],
	received: new Date(Date.now()),
};

const shouldHideAlert = (): boolean => {
	// eslint-disable-next-line global-require
	const store = require('../main');
	if (!store.default) return false;

	const {
		SessionReducer: {
			user,
		},
		MeetingReducer: {
			roomState,
		},
		ShotsReducer: {
			currentShotId,
			shots,
		},
	} = store.default.getState() as GreenroomState;
	if (roomState === RoomStates.showbuilder) return false;

	const currentShot = shots.find((s: Shot) => s.shot_id === currentShotId);
	if (!currentShot) return false;

	const userIsOnCanvas = !!currentShot.shot_data.shotFeeds.find(
		(feed: ShotFeed) => feed.presenter === user?.gr_user,
	);

	return userIsOnCanvas;
};

function handleAlert(payload: any, connUser?: any, moderated: boolean = false) {
	const shouldHide = shouldHideAlert();
	if (shouldHide) return;

	const messageSource = !moderated
		? getNameOrInitials(payload.sender, true)
		: payload.name;

	const chatAlert = Alerts.showAlert({
		message: messageSource,
		description: !moderated ? payload.message : stripHtml(payload.comment),
		duration: 4000,
		type: 'neutral',
		onClick: () => {
			// eslint-disable-next-line global-require
			const store = require('../main');
			store.default.dispatch(setActiveTab(TABS.CHAT));
			store.default.dispatch(setRightDrawerOpen(true));
			if (!moderated) {
				if (payload.room !== undefined) store.default.dispatch(setSelectedRoom(payload.room));
				if (connUser) store.default.dispatch(setSelectedUser(connUser));
			} else {
				store.default.dispatch(viewModeratedComments(true));
			}
			Alerts.hideAlert({ id: chatAlert });
		},
	});
}

const actions: Map<any, Function> = new Map([
	[
		CLEAR_CHAT_DIRECT_CHAT,
		(state: any) => {
			const directMessages = { ...state.directMessages };
			const keys = Object.keys(state.directMessages);
			keys.forEach((key) => {
				directMessages[key] = [];
			});
			return {
				...state,
				directMessages,
			};
		},
	],
	[
		CLEAR_CHAT_DIRECT_CHAT_KEY,
		(state: any, action: Action) => {
			const directMessages = { ...state.directMessages };
			const convo = directMessages[action.payload.key];

			if (!convo) {
				return state;
			}
			if (!action.payload.all && (convo?.messages?.length > 0)) {
				// Keep only the first message
				directMessages[action.payload.key] = {
					...convo,
					messages: [convo.messages[0]],
				};
			} else {
				// Clear all messages
				directMessages[action.payload.key] = {
					...convo,
					messages: [],
					meta: { totalMessageCount: 0 },
				};
			}

			return {
				...state,
				directMessages,
			};
		},
	],
	[
		LOAD_CHAT_DIRECT_CHAT,
		(state: any, action: Action) => handle(state, action, {
			success: (state) => {
				const newMessagesByKey = action.payload as ChatConvo;

				const updatedMessages = { ...state.directMessages };

				Object.entries(newMessagesByKey).forEach(([key, incoming]: [string, ChatConvo]) => {
					if (!updatedMessages[key]) {
						updatedMessages[key] = {
							messages: [],
							meta: {
								totalMessageCount: 0,
							},
						};
					}

					const existingMessages = updatedMessages[key].messages;
					const mergedMessages = [
						...existingMessages,
						...incoming.messages.filter(
							(msg) => !existingMessages.some((e: ChatPost) => e.id === msg.id),
						),
					];

					updatedMessages[key] = {
						...updatedMessages[key],
						messages: mergedMessages,
						meta: incoming.meta,
					};
				});

				return {
					...state,
					directMessages: updatedMessages,
				};
			},
		}),
	],
	[
		LOAD_ROOM_CHAT,
		(state: any, action: Action) => handle(state, action, {
			success: (state) => {
				if (!action || !action.payload) return { ...state, rooms: [] };

				return { ...state, rooms: action.payload ?? [] };
			},
		}),
	],
	[
		POST_CHAT,
		(state: ChatReducerState, action: Action): ChatReducerState => handle(state, action, {
			success: (state) => {
				const { roomId, insertId } = action.payload;
				if (roomId !== undefined && insertId) {
					const newRooms = [...state.rooms].map((room: ChatRoom) => {
						if (room.id === roomId) {
							// eslint-disable-next-line no-param-reassign
							room.messages = room.messages.map((message: ChatPost) => {
								if (!message.id) {
									// eslint-disable-next-line no-param-reassign
									message.id = insertId;
								}

								return message;
							});
						}

						return room;
					});

					return { ...state, rooms: newRooms };
				}

				return { ...state };
			},
		}),
	],
	[
		CREATE_ROOM,
		(state: any, action: Action) => handle(state, action, {
			success: (state) => {
				return {
					...state,
					rooms: [...state.rooms, action.payload],
				};
			},
		}),
	],
	[
		NEW_MESSAGE,
		(state: ChatReducerState, action: Action): ChatReducerState => {
			const newState = { ...state };
			const parsedPayload = action.payload;

			// if message is coming from me, mark it as seen
			// eslint-disable-next-line no-param-reassign
			parsedPayload.seen = parsedPayload.sender.gr_user === newState.gr_user;

			// if receiving a message and you changed tabs
			// eslint-disable-next-line no-param-reassign
			parsedPayload.openTab = parsedPayload.openTab
				? parsedPayload.openTab
				: 'chat';

			// since seen is already set to true if you're on the chat, it still needs to call seen api
			// need to identify this as seen but still mark as a new message that needs to call seen api
			// so mark this as a new message if openTab is chat and the sender is not the current user
			// eslint-disable-next-line no-param-reassign
			parsedPayload.isNew = newState.gr_user !== parsedPayload.sender.gr_user
				&& parsedPayload.openTab === 'chat';
			if (typeof parsedPayload.room !== 'undefined') {
				const room = newState.rooms.find(
					(room: ChatRoom) => room.id === parsedPayload.room,
				);
				// eslint-disable-next-line no-param-reassign
				parsedPayload.seen = parsedPayload.room === newState.selectedRoom
					&& parsedPayload.openTab === 'chat';

				if (room) {
					// only set the message id if the payload doesn't already have a message id
					if (!parsedPayload.id) {
						const newMessageId = room.messages?.length
							? room.messages
								.map((message) => message.id)
								.sort((a, b) => b - a)[0] + 1
							: 0;

						if (newMessageId) {
							// eslint-disable-next-line no-param-reassign
							parsedPayload.id = newMessageId;
						}
					}

					room.messages = [parsedPayload, ...room.messages];
				}
			} else {
				const id = makeId([
					parsedPayload.sender.email,
					parsedPayload.recipient.email,
				]);
				const currentConvo = newState.directMessages[id];
				const chatId: number = currentConvo?.messages.reduce<number>(
					(highest: number, chatPost: ChatPost) => {
						const { id } = chatPost;

						if (id > highest) {
							return id;
						}

						return highest;
					},
					0,
				) ?? 0;

				/*
			This is a quick fix for setDirectMessagesSeen so we know what id to
			mark as read. If this is revisited, another more robust solution is
			sending a socket message from the back end instead of the front end
			every time a message sent, so we have the id and don't need to set
			it here. That would likely require more substantial refactoring,
			however, and this will work with the current setup.
			*/
				// eslint-disable-next-line no-param-reassign
				parsedPayload.id = parsedPayload.id ? parsedPayload.id : chatId + 1;
				// eslint-disable-next-line no-param-reassign
				parsedPayload.seen = (parsedPayload.sender.gr_user === newState.selectedUser?.gr_user
					|| parsedPayload.recipient.gr_user === newState.selectedUser?.gr_user)
					&& parsedPayload.openTab === 'chat';

				const currentCount = currentConvo?.meta?.totalMessageCount ?? 0;

				if (newState.directMessages[id]) {
					newState.directMessages = {
						...newState.directMessages,
						[id]: {
							...newState.directMessages[id],
							messages: [parsedPayload, ...newState.directMessages[id].messages],
							meta: {
								totalMessageCount: currentCount + 1,
							},
						},
					};
				} else {
					newState.directMessages[id] = {
						messages: [parsedPayload],
						meta: {
							totalMessageCount: 1,
						},
					};
				}
			}

			// don't update lastUnseen if message came from me
			if (parsedPayload.sender.gr_user !== newState.gr_user) {
				newState.lastUnseen = Date.now();
			}

			return newState;
		},
	],
	[
		NEW_ROOM,
		(state: any, action: Action) => {
			return {
				...state,
				rooms: [...state.rooms, action.payload],
			};
		},
	],
	[
		SET_SELECTED_ROOM,
		(state: any, action: Action) => ({
			...state,
			selectedRoom: action.payload,
			selectedUser: null,
		}),
	],
	[
		SET_SELECTED_USER,
		(state: any, action: Action) => {
			// this isn't a perfect fix - however it should keep the UI from getting stuck in a bad state
			if (!action.payload?.email) {
				return { ...state, selectedUser: null, selectedRoom: null };
			}

			return { ...state, selectedUser: action.payload, selectedRoom: null };
		},
	],
	[
		LOAD_CHAT_ROOM_MESSAGES,
		(state: any, action: Action) => {
			if (!action || !action.payload) return { ...state };

			const newMessages = action.payload ?? [];

			const updatedRooms = [...state.rooms];

			newMessages.forEach((msg: any) => {
				const room = updatedRooms.find((r) => r.id === msg.room);

				if (!room) return;

				if (!room.messages) {
					room.messages = [];
				}

				const existingIds = new Set(room.messages.map((m: any) => m.id));

				if (!existingIds.has(msg.id)) {
					room.messages.push(msg);
				}
			});

			return {
				...state,
				rooms: updatedRooms,
			};
		},
	],
	[
		CLEAR_CHAT_ROOM_MESSAGES,
		(state: any, action: Action) => {
			if (!action || action.payload === undefined || action.payload === null) return { ...state };
			const rooms = [...state.rooms];
			const roomIndex = rooms.findIndex((room: ChatRoom) => room.id === action.payload.roomId);

			if (roomIndex !== -1) {
				const room = rooms[roomIndex];
				const firstMessage = room.messages?.[0];
				rooms[roomIndex] = {
					...room,
					messages: !action.payload.all && firstMessage ? [firstMessage] : [],
				};
			}
			return {
				...state,
				rooms,
			};
		},
	],
	[
		DELETE_CHAT_ROOM,
		(state: any, action: Action) => {
			if (!action || action.payload === undefined || action.payload === null) return { ...state };

			const roomIdToDelete = action.payload.id || action.payload;
			const updatedRooms = state.rooms.filter((room: ChatRoom) => room.id !== roomIdToDelete);

			return {
			...state,
			rooms: updatedRooms,
			};
		},
	],
	[
		CREATE_CONVO,
		(state: any, action: Action) => {
			const [user, connUser] = action.payload;
			const id = makeId([user.email, connUser.email]);
			const newDirect = { ...state.directMessages };
			newDirect[id] = [];

			return {
				...state,
				directMessages: newDirect,
				selectedUser: connUser,
				selectedRoom: null,
			};
		},
	],
	[
		SET_DIRECT_MESSAGES_SEEN,
		(state: ChatReducerState, action: Action): ChatReducerState => handle(state, action, {
			success: (state) => {
				const payload = action.payload as MessagesSeenResult | boolean;

				if (typeof payload === 'object') {
					const newDirect = { ...state.directMessages };
					const { email } = { ...state.selectedUser };
					const directMessageKey = email && Object.keys(newDirect)
						.find((key) => key?.includes(email));

					if (directMessageKey) {
						const convo = newDirect[directMessageKey];
						if (!convo) {
							console.warn(directMessageKey, 'convo does not exist');
							return { ...state };
						}

						const updatedMessages = convo.messages.map((message: ChatPost) => ({
							...message,
							seen: true,
							isNew: false,
						}));

						newDirect[directMessageKey] = {
							...convo,
							messages: updatedMessages,
						};

						return {
							...state,
							directMessages: newDirect,
							lastUnseen: Date.now(),
						};
					}
				}

				return { ...state };
			},
		}),
	],
	[
		SET_ROOM_MESSAGES_SEEN,
		(state: ChatReducerState, action: Action): ChatReducerState => handle(state, action, {
			success: (state) => {
				const payload = action.payload as MessagesSeenResult | boolean;

				if (typeof payload === 'object') {
					const newRooms = [...state.rooms].map((room: ChatRoom) => {
						if (room.id === payload.id) {
							// eslint-disable-next-line no-param-reassign
							room.messages = room.messages.map((message: ChatPost) => {
								// eslint-disable-next-line no-param-reassign
								message.seen = true;
								// eslint-disable-next-line no-param-reassign
								message.isNew = false;
								return message;
							});
						}

						return room;
					});

					return { ...state, rooms: newRooms, lastUnseen: Date.now() };
				}

				return { ...state };
			},
		}),
	],
	[
		SET_MODERATED_LEGACY_COMMENTS_SEEN,
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		(state: ChatReducerState, action: Action) => {
			const modComments = { ...state.legacyModeratedComments };

			// eslint-disable-next-line no-restricted-syntax, guard-for-in
			for (const key in modComments) {
				modComments[key] = { ...modComments[key], seen: true };
			}

			return { ...state, legacyModeratedComments: modComments };
		},
	],
	[
		SET_MODERATED_EVENTS_MESSAGES_SEEN,
		(state: ChatReducerState, action: Action): ChatReducerState => handle(state, action, {
			success: (state) => {
				/*
				action.payload looks like:
				{
					chatMessages: number[],
					questions: number[],
					comments: number[]
				}
				`number[]` represents the IDs affected in the E3 db.
				We could use this, but it's easier to just update the
				store directly on success.
				*/
				const updatedModeratedEventsMessages = new Map(
					state.moderatedEventsMessages,
				);

				// eslint-disable-next-line no-restricted-syntax
				for (const [messageKey, messageObject] of Array.from(
					updatedModeratedEventsMessages.entries(),
				)) {
					messageObject.seen = true;
					updatedModeratedEventsMessages.set(messageKey, messageObject);
				}

				return {
					...state,
					moderatedEventsMessages: updatedModeratedEventsMessages,
				};
			},
		}),
	],
	[
		SET_GR_USER,
		(state: any, action: Action) => ({ ...state, gr_user: action.payload }),
	],
	[
		GET_MODERATED_COMMENTS,
		(state: any, action: Action) => handle(state, action, {
			success: (state) => {
				const comms = action.payload;
				// eslint-disable-next-line no-restricted-syntax, guard-for-in
				for (const key in comms) {
					comms[key].received = new Date();
				}
				return { ...state, legacyModeratedComments: comms };
			},
		}),
	],
	[
		GET_APPROVED_COMMENT,
		(state: any, action: Action) => handle(state, action, {
			success: (state) => {
				const comms = action.payload;
				// eslint-disable-next-line no-restricted-syntax, guard-for-in
				for (const key in comms) {
					comms[key].received = new Date();
					comms[key].seen = false;
				}
				const newState = {
					...state,
					legacyModeratedComments: {
						...state.legacyModeratedComments,
						...comms,
					},
				};
				return newState;
			},
		}),
	],
	[
		UNELEVATE_COMMENT,
		(state: any, action: Action) => {
			const comms = { ...state.legacyModeratedComments };
			if (comms[action.payload]) comms[action.payload].elevated = 0;
			return { ...state, legacyModeratedComments: comms };
		},
	],
	[
		ELEVATE_COMMENT,
		(state: any, action: Action) => {
			const comms = { ...state.legacyModeratedComments };
			if (comms[action.payload]) comms[action.payload].elevated = 1;
			if (comms[action.payload]) comms[action.payload].received = new Date();
			return { ...state, legacyModeratedComments: comms };
		},
	],
	[
		DELETE_COMMENT,
		(state: any, action: Action) => {
			const comms = { ...state.legacyModeratedComments };
			if (comms[action.payload]) comms[action.payload].deleted = 1;
			return { ...state, legacyModeratedComments: comms };
		},
	],
	[
		UNDELETE_COMMENT,
		(state: any, action: Action) => {
			const comms = { ...state.legacyModeratedComments };
			if (comms[action.payload]) comms[action.payload].deleted = 0;
			return { ...state, legacyModeratedComments: comms };
		},
	],
	[
		VIEW_MODERATED_COMMENTS,
		(state: any, action: Action) => ({
			...state,
			shouldViewModerated: action.payload,
		}),
	],
	[
		CREATE_MODERATED_EVENTS_MESSAGES,
		(state: ChatReducerState, action: Action) => {
			/*
		A map is used here not just because it's easy to copy from E3
		(which it is), but also because it makes finding and deleting messages
		fairly simple. An array would be another reasonably easy option to use,
		but aside from the unshifting, this seems to work well.
		*/
			const {
				key,
				eventsMessage,
			}: {
				key: string;
				eventsMessage: GeneralEventsMessage;
			} = action.payload;
			const updatedMessageArray = Array.from(state.moderatedEventsMessages);

			updatedMessageArray.unshift([key, eventsMessage]);

			const updatedModeratedEventsMessages = new Map(updatedMessageArray);

			return {
				...state,
				moderatedEventsMessages: updatedModeratedEventsMessages,
			};
		},
	],
	[
		DELETE_MODERATED_EVENTS_MESSAGES,
		(state: ChatReducerState, action: Action) => {
			const key: string = action.payload;
			const updatedModeratedEventsMessages = new Map(
				state.moderatedEventsMessages,
			);

			updatedModeratedEventsMessages.delete(key);

			return {
				...state,
				moderatedEventsMessages: updatedModeratedEventsMessages,
			};
		},
	],
	[
		GET_PRESENTER_MESSAGES,
		(state: ChatReducerState, action: Action) => handle(state, action, {
			success: (state) => {
				const loadedPresenterMessages: GeneralEventsMessage[] = action.payload;

				const updatedModeratedEventsMessages = new Map(
					state.moderatedEventsMessages,
				);

				if (!updatedModeratedEventsMessages.size) {
					// eslint-disable-next-line no-restricted-syntax
					for (const message of loadedPresenterMessages) {
						updatedModeratedEventsMessages.set(
							`${message.message_type}-${message.id}`,
							message,
						);
					}
				}

				return {
					...state,
					moderatedEventsMessages: updatedModeratedEventsMessages,
				};
			},
		}),
	],
	[
		REMOVE_CHAT_MESSAGE,
		(state: any, action: Action) => {
			const newState = { ...state };
			const chatFilter = (message: ChatPost) => message.id !== action.payload.messageId;

			if (action.payload.roomId !== undefined) {
				newState.rooms = newState.rooms.map((room: ChatRoom) => ({
					...room,
					messages: room.messages.filter(chatFilter),
				}));
			}

			if (action.payload.dmKey && newState.directMessages[action.payload.dmKey]) {
				newState.directMessages = {
					...newState.directMessages,
					[action.payload.dmKey]: {
						...newState.directMessages[action.payload.dmKey],
						messages: newState.directMessages[action.payload.dmKey].messages.filter(chatFilter),
					},
				};
			}

			return newState;
		},
	],
]);

const maskAwareName = (
	u: any, // should be reference to sender object prop in payload object
	activeMasks: Record<string, number> | undefined,
	masks: Record<string, ProfileMask>
) => {
	if (!u?.email || !activeMasks)
		return;
	const mask = masks[activeMasks[u.email]];
	if (!mask)
		return
	if (u.first_name !== undefined)
		u.first_name = mask.first_name ?? '';
	if (u.last_name !== undefined)
		u.last_name = mask.last_name ?? '';
}

// eslint-disable-next-line default-param-last
export function ChatReducer(state: ChatReducerState = initialChatState, action: Action) {
	const newState = actions.get(action.type)?.(state, action) || state;

	setTimeout(() => {
		// eslint-disable-next-line global-require
		const store = require('../main');
		if (!store.default) return;

		const {
			SessionReducer: {
				user,
				connectedUsers,
			},
			TabsReducer: {
				activeTab
			},
			MasksReducer: {
				profileMasks,
				activeMasksEntity
			}
		} = store.default.getState() as GreenroomState;

		if (!user) return;

		if (action.type === NEW_MESSAGE) {
			const sanitizedPayload = JSON.stringify(action.payload);
			const parsedPayload = JSON.parse(sanitizedPayload);
			maskAwareName(parsedPayload.sender, activeMasksEntity?.entityValue, profileMasks);
			if (typeof parsedPayload.room !== 'undefined') {
				const room = newState.rooms.find((room: ChatRoom) => room.id === parsedPayload.room);
				if (room && activeTab !== 'chat' && parsedPayload.sender.gr_user !== user.gr_user) {
					handleAlert(parsedPayload);
				} else if (
					room
					&& newState.selectedRoom !== room.id
					&& parsedPayload.sender.gr_user !== user.gr_user
				) {
					handleAlert(parsedPayload);
				}
			} else if (
				(
					parsedPayload.sender.gr_user !== user.gr_user
					&& parsedPayload.sender.email !== newState.selectedUser?.email
				)
				|| (parsedPayload.sender.gr_user !== user.gr_user && activeTab !== 'chat')
			) {
				const connUser = connectedUsers.find(
					(user: any) => user.gr_user === parsedPayload.sender.gr_user,
				);
				handleAlert(parsedPayload, connUser);
			}
		} else if (
			action.type === GET_APPROVED_COMMENT
			&& action.payload
			&& (activeTab !== 'chat' || !state.shouldViewModerated)
		) {
			const msg = Object.values(action.payload)[0] as ChatMessage;
			maskAwareName(msg, activeMasksEntity?.entityValue, profileMasks);
			handleAlert(
				{...defaultChatMessage, ...(msg ?? {})},
				undefined,
				true,
			);
		} else if (
			action.type === CREATE_MODERATED_EVENTS_MESSAGES
			&& action.payload
			&& (activeTab !== 'chat' || !state.shouldViewModerated)
		) {
			const { eventsMessage }: {
				key: string;
				eventsMessage: GeneralEventsMessage;
			} = action.payload;
			maskAwareName(eventsMessage, activeMasksEntity?.entityValue, profileMasks);
			const { name, message } = getDisplayChatMessage(eventsMessage);
			const eventsMessageAlert: { name: string; comment: string } = {
				name,
				comment: message,
			};
			handleAlert(eventsMessageAlert, undefined, true);
		}
	}, 250);

	return newState;
}
